import React from 'react';

import {
    Chip,
    Grid,
    Typography
} from '@mui/material';
import { Info } from '@mui/icons-material';

import { JobType, OrderType, MissingRequirementsType } from '../../types';
import { getFormattedOrderExpiryDate } from '../../utils/order';
import OfferFormFields from './OfferFormFields';
import SimpleForm from '../CommonComponents/Form/SimpleForm';
import { useChangeable } from '../CommonComponents/Form';
import { InfoText } from '../CommonComponents';
import toastMessage from '../../utils/toast';
import { createOffer } from '../../api/offers';
import NotifySentOfferPopup from './NotifySentOfferPopup';
import { defaultMissingRequirements } from '../../utils/metadata';
import OfferPopupTenantNotQualified from './OfferPopupTenantNotQualified';
import { updateTenantMetadata } from '../../api/tenants';

export const defaultOffer = {
    price: '',
    expiresAt: '',
};

type PropsType = {
    order: OrderType | JobType;
    refetch: () => void;
    missingRequirements?: MissingRequirementsType;
}

export default function OfferForm({ order, refetch, missingRequirements = defaultMissingRequirements }: PropsType) {
    const [newOffer, onChange] = useChangeable(defaultOffer);
    const [showNotifySentPopup, setShowNotifySentPopup] = React.useState(false);
    const [showMissingRequirementsPopup, setShowMissingRequirementsPopup] = React.useState(false);

    const sendOffer = React.useCallback(async (message?: string) => {
        try {
            if (message) {
                await createOffer({ orderId: order.id, body: { ...newOffer, message } });
            } else {
                await createOffer({ orderId: order.id, body: { ...newOffer } });
            }
            setShowNotifySentPopup(true);
        } catch (e) {
            toastMessage(e, 'Kunde inte skicka prisförslag, försök igen.');
            refetch();
        }
    }, [newOffer, order.id, refetch]);

    const closeNotifySentPopup = React.useCallback(() => {
        setShowNotifySentPopup(false);
        refetch();
    }, [refetch]);

    const handleSenderIsQualified = React.useCallback(async () => {
        try {
            await updateTenantMetadata({
                vehicleTypes: missingRequirements.missingVehicleTypes,
                equipment: missingRequirements.missingEquipment,
                sustainability: missingRequirements.missingSustainability,
                licencesCertificates: missingRequirements.missingLicencesCertificates,
                customLicencesCertificates: missingRequirements.missingCustomLicencesCertificates
            });
        } catch (e) {
            toastMessage(e, 'Kunde inte uppdatera företagets profil, försök igen.');
            return;
        }
        sendOffer();
        setShowMissingRequirementsPopup(false);
    }, [missingRequirements, sendOffer]);

    const handleSenderIsNotQualified = React.useCallback(() => {
        sendOffer();
        setShowMissingRequirementsPopup(false);
    }, [sendOffer]);

    return (
        <Grid container spacing={1}>
            <NotifySentOfferPopup
                open={showNotifySentPopup}
                handleClose={closeNotifySentPopup}
                expiresAt={newOffer.expiresAt}
                price={newOffer.price}
            />
            <OfferPopupTenantNotQualified
                open={showMissingRequirementsPopup}
                handleClose={() => setShowMissingRequirementsPopup(false)}
                missingRequirements={missingRequirements}
                handleSenderIsQualified={handleSenderIsQualified}
                handleSenderIsNotQualified={handleSenderIsNotQualified}
            />
            {/* )} */}
            <Grid item xs>
                <Typography variant="h6" color="primary">
                    Prisförslag
                </Typography>
            </Grid>
            <Grid item>
                <Chip
                    icon={<Info />}
                    label={`Skicka senast ${getFormattedOrderExpiryDate(order)}`}
                />
            </Grid>

            <Grid item xs={12}>
                {order.isOpen ? (
                    <SimpleForm
                        onSubmit={
                            !missingRequirements.isMissingRequirements
                                ? () => sendOffer()
                                : () => setShowMissingRequirementsPopup(true)
                        }
                        submitText="Föreslå pris"
                        submitDisabled={!newOffer.price || !order.isOpen}
                        useGrid
                    >
                        <OfferFormFields
                            price={newOffer.price}
                            expiresAt={newOffer.expiresAt}
                            onChange={onChange}
                            listPrice={order.listPrice}
                            disabled={!order.isOpen}
                        />
                    </SimpleForm>
                ) : (
                    <InfoText showBorder>
                        Uppdraget tar inte längre emot prisförslag.
                    </InfoText>
                )}
            </Grid>
        </Grid>
    );
}
